import { Vue } from "vue-class-component";
import { useStore, Store } from 'vuex';
import { useToast } from "primevue/usetoast";
import { State } from '../store';
import { useRouter, useRoute } from "vue-router";

// Mock GetSession if web-sdk is not available
let GetSession: () => any;

// Using dynamic import to avoid ESLint errors
// This returns a Promise, which is properly handled in TypeScript/ESLint
const importWebSdk = async () => {
  try {
    const webSdk = await import("web-sdk").catch(() => null);
    if (webSdk) {
      GetSession = webSdk.GetSession;
      return true;
    }
    return false;
  } catch (e) {
    console.warn("web-sdk not available, using mock GetSession");
    return false;
  }
};

// Set up fallback implementation
GetSession = () => ({
  iss: 0,
  services: [],
  user: window.localStorage.getItem('user') ? JSON.parse(window.localStorage.getItem('user') || '{}') : {}
});

// Try to load the module asynchronously
importWebSdk().then(success => {
  if (!success) {
    console.warn("Using mock GetSession implementation");
  }
});


export const masterTypes = [
    { name : "Source ID", code: "source_id"},
    { name : "Lead Status", code: "lead_status"},
    { name : "Interaction Status", code: "interaction_status"},
    { name : "Resource Name", code : "resource_name"},
    { name : "Customer Type", code: "customer_type"},
    { name : "Phone Type", code: "phone_type"},
    { name : "User Role", code: "user_role"},
    { name : "Branch Name", code: "branch"},
    { name : "Lead Source", code:"lead_source"},
    { name : "Direction Type", code:"direction_type"},
    { name : "Business Rule Type", code: "rule_type"},
    { name : "Region", code: "region"},
    { name : "Organization", code: "organization"},
    { name : "Country Code", code: "country_code"}
]

export function sortObjectsByPriority(arr : any[]) {
    return arr.sort((a, b) => b.priority - a.priority);
}

export const priorities =  [
    {
        label: "Low", 
        code: 0,
    },
    {
        label: "Neutral", 
        code: 1,
    },
    {
        label: "High", 
        code: 2,
    }
]

export function formatToIndianNotation(number: number, options : any = {}) {
    // Convert number to string
    if(!number)
      return "0"

    return new Intl.NumberFormat('en-IN', options).format(number);
}

export function objectToQueryString(obj : any) {
    return Object.keys(obj)
    .map(key => {
      let value = obj[key];
      if (value instanceof Date) {
        value = value.toISOString();
      }
      return encodeURIComponent(key) + '=' + encodeURIComponent(value);
    })
    .join('&');
}

export function addSpaceBetweenLowerAndCapital(input: string): string {

    return input.replace(/([a-z])([A-Z])/g, '$1 $2');
}

export function removeParentIfContainsAsterisks(obj: any): any {
    if (Array.isArray(obj)) {
        return obj.reduce((acc, item) => {
            const result = removeParentIfContainsAsterisks(item);
            if (result !== undefined) {
                acc.push(result);
            }
            return acc;
        }, [] as any[]);
    } else if (typeof obj === 'object' && obj !== null) {
        const keys = Object.keys(obj);
        for (const key of keys) {
            // eslint-disable-next-line
            if (typeof obj[key] === 'string' && /.*\*.*[^\*].*/.test(obj[key])) {
                return undefined; // Remove this entire object
            } else {
                obj[key] = removeParentIfContainsAsterisks(obj[key]);
            }
        }
    }
    return obj;
}

export class base extends Vue {

    store! : Store<State>
    public toast = useToast();
    route = useRoute();
    public router = useRouter();
    validMap : any = {}
    
    // Remove the $ properties to avoid Vue warnings about reserved properties
    // Use the non-$ versions directly in your component methods


    handleSuccess(title: string, message : string){

        this.toast.add({
            severity:'success', 
            summary: title,
            detail: message,
            life: 5000,
        });
    }

    handleWarning(title: string, message: string){

        this.toast.add({
            severity:'warn',
            summary: title,
            detail: message,
            life: 5000,
        });
    }

    handleError(e : any){

        this.validMap = {}
        const msg = e.response?.data?.error ? e.response.data.error : e 
       

        const validationErrors = this.extractKeyAndError(msg)

        if(!validationErrors.length){
            this.toast.add({
                severity:'error', 
                summary:'Error completing operation',
                detail: msg,
            });
            return
        }

        for(const v of validationErrors){

            const titleFormatted = addSpaceBetweenLowerAndCapital(
                v.key.split(".").join(" > "),
            )

            this.toast.add({
                severity:'error', 
                summary: titleFormatted,
                detail: v.error,
            });

            const key = v.key.split(".")[1]

            this.validMap[key] = v.error
        }
       
    }

    extractKeyAndError(inputString : string) {
        const regex = /^Key: '([^']+)' Error:(.*)$/gm;
        let matches;
        const result : any[] = [];
    
        // Check if the input string matches the expected format
        if (!regex.test(inputString)) {
            return result; // Return an empty array if the input string doesn't match the format
        }
    
        // Reset the regex to its initial state
        regex.lastIndex = 0;
    
        // Extract key and error
        while ((matches = regex.exec(inputString)) !== null) {
            const [, key, error] = matches;
            result.push({
                key: key.trim(),
                error: error.trim()
            });
        }
    
        return result;
    }

    isAdmin(){

        const jwt = GetSession()
        return jwt.iss > 0
    }

    isLoggedIn(){
        return this.isAdmin()
    }
    

    created(){
        this.store = useStore()
    }
}
